import classNames from 'classnames';
import { formatEther } from 'ethers/lib/utils';
import { useTranslation } from 'next-i18next';
import React, { VFC } from 'react';

import useETHPrice from '@/hooks/useETHPrice';

import styles from './CreationSaleCard.module.scss';

type Props = {
  totalEditions: number;
  availableTokenAmount: number;
  ethSalePrice: string;

  purchaseAmount: number;
  onPurchaseAmountChange(number): void;

  isCancelled: boolean;
  isNotOnSale: boolean;
  isNotReleased: boolean;
  isProcessing: boolean;

  onBuyButtonClick(): void;

  ticker?: 'ETH';
};

const CreationSaleCard: VFC<Props> = ({
  totalEditions,
  availableTokenAmount,
  ethSalePrice,

  purchaseAmount,
  onPurchaseAmountChange,

  isCancelled,
  isNotOnSale,
  isNotReleased,
  isProcessing,

  onBuyButtonClick,

  ticker = 'ETH',
}: Props) => {
  const { t, i18n } = useTranslation();

  const { ethToUSD, ethToJPY, convertETHToCurrency } = useETHPrice();
  
  const isBuyButtonDisabled =
    isCancelled || isNotOnSale || isNotReleased || !availableTokenAmount;

  let buyButtonText = t('creationPage.buy');
  if (isCancelled || isNotOnSale) {
    buyButtonText = 'Not On Sale';
  } else if (isNotReleased) {
    buyButtonText = 'Not Released Yet';
  }

  const subCurrency = i18n.language === 'ja' ? 'JPY' : 'USD';
  const isCurrencyReady =
    (subCurrency === 'USD' && ethToUSD !== null) ||
    (subCurrency === 'JPY' && ethToJPY !== null);

  // All checks for availableTokenAmount were changed from === to <= for a temporary fix
  // on rendering issues where we were getting odd values from the graph
  return (
    <div className={styles.creationSaleCard}>
      <dl>
        <dt>{t('creationPage.totalEditions')}</dt>
        <dd>{totalEditions}</dd>

        <dt>{t('creationPage.availableEditions')}</dt>
        <dd>
          {isCancelled ? (
            'Cancelled'
          ) : (
            <>
              {availableTokenAmount <= 0 ? 'Sold out' : availableTokenAmount}
            </>
          )}
        </dd>

        <dt>{t('creationPage.amount')}</dt>
        <dd>
          {isCancelled ? (
            'Cancelled'
          ) : (
            <>
              {availableTokenAmount <= 0 ? (
                'Sold out'
              ) : (
                <select
                  id={styles.selectStyle}
                  disabled={availableTokenAmount <= 0}
                  onChange={(event) => {
                    onPurchaseAmountChange(event.target.value);
                  }}
                  className={styles.selectPurchaseAmount}
                >
                  {/* temp fix: broke the rendering  */}
                  {availableTokenAmount >= 0 && Array.from(Array(availableTokenAmount).keys()).map((i) => (
                    <option key={i + 1} value={i + 1}>
                      {i + 1}
                    </option>
                  ))}
                </select>
              )}
            </>
          )}
        </dd>
      </dl>

      <dl className={styles.pricingList}>
        <dt>{t('creationPage.unitPrice')}</dt>
        <dd>
          {formatEther(ethSalePrice)} {ticker}{' '}
          {isCurrencyReady ? (
            <span className={styles.subCurrency}>
              ≈ {convertETHToCurrency(ethSalePrice, subCurrency)} {subCurrency}
            </span>
          ) : null}
        </dd>

        <dt>{t('creationPage.price')}</dt>
        <dd>
          {Number(formatEther(ethSalePrice)) * purchaseAmount} {ticker}
          {isCurrencyReady ? (
            <span className={styles.subCurrency}>
              ≈{' '}
              {convertETHToCurrency(
                (Number(ethSalePrice) * purchaseAmount).toString(),
                subCurrency
              )}{' '}
              {subCurrency}
            </span>
          ) : null}
        </dd>
      </dl>

      <button
        type="button"
        onClick={onBuyButtonClick}
        disabled={isBuyButtonDisabled || isProcessing}
        className={classNames(styles.buyButton, {
          [styles.buyButtonLoading]: isProcessing,
        })}
      >
        {buyButtonText}
      </button>
    </div>
  );
};

export default CreationSaleCard;
